'use client';

import { FormEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { CHAT_LABELS } from '@/app/lib/guideChat/labels';
import { quickRepliesFor } from '@/app/lib/guideChat/quickReplies';
import type { AskResponse, Citation, SubjectNotice, UnavailableResponse } from '@/app/lib/guideChat/types';
import AppointmentCard, { AppointmentCardData } from './AppointmentCard';
import MarkdownLite from './MarkdownLite';
import PhoneCtaLink, { phoneCtaOf } from './PhoneCtaLink';
import UnavailableNotice from './UnavailableNotice';
import { useFocusTrap } from './useFocusTrap';

export interface ChatTurn {
  id: string;
  question: string | null;
  notice?: string;
  loading?: boolean;
  response?: AskResponse;
  unavailable?: UnavailableResponse;
  error?: string;
  feedback?: 'up' | 'down';
}

interface QuoteCta {
  title?: string;
  message?: string;
  label?: string;
  url?: string;
}

interface Props {
  open: boolean;
  turns: ChatTurn[];
  busy: boolean;
  // The backend ended the conversation: nothing more can be sent until the visitor starts a new one.
  closed?: boolean;
  conversationId?: string | null;
  suggestions: string[];
  launcherRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onSend: (question: string) => void;
  onFeedback: (turnId: string, rating: 'up' | 'down') => void;
  onSwitchToForm: (prefillUrl: string) => void;
  onReset: () => void;
}

// The « Sources : [1], [3] » line is for the judge and the admin debug; the widget already lists the sources as links.
function withoutSourcesLine(answer: string): string {
  return answer.replace(/\n*\s*\*{0,2}Sources?\s*:\s*\*{0,2}\s*(\[\d+\][\s,]*)+\.?\s*\*{0,2}\s*$/i, '').trimEnd();
}

function citationHref(c: Citation): string {
  const url = typeof c.url === 'string' ? c.url : '';
  const anchor = typeof c.anchor === 'string' && c.anchor ? `#${c.anchor}` : '';
  return `${url}${anchor}`;
}

function citationLabel(c: Citation): string {
  const label = (typeof c.breadcrumb === 'string' && c.breadcrumb) || c.title || citationHref(c);
  return typeof c.sourceLabel === 'string' && c.sourceLabel ? `${c.sourceLabel} · ${label}` : label;
}

function noticeOf(notice: SubjectNotice | string | null | undefined): SubjectNotice | null {
  if (!notice) return null;
  return typeof notice === 'string' ? { message: notice, url: null } : notice;
}

// A topic that scripted the reframe drops the notice box, but the dedicated journey link must stay under the answer.
function subjectLinkOf(response: AskResponse, notice: SubjectNotice | null): string | null {
  if (notice?.url) return notice.url;
  const redirect = response.subjectRedirect as { url?: unknown } | null | undefined;
  return !notice && redirect && typeof redirect.url === 'string' && redirect.url ? redirect.url : null;
}

function isAppointmentCard(card: unknown): card is AppointmentCardData {
  return !!card && typeof card === 'object' && Array.isArray((card as AppointmentCardData).slots);
}

function quoteCtaOf(cta: unknown): QuoteCta | null {
  return cta && typeof cta === 'object' && typeof (cta as QuoteCta).url === 'string' ? (cta as QuoteCta) : null;
}

const COMPARATOR_URL = '/devis-assurance-pret-immobilier';

const secondaryButton =
  'rounded-lg border border-rm-blue-deep bg-white px-3 py-1.5 text-sm font-semibold text-rm-blue-deep hover:bg-rm-blue-pale focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue-deep';

export default function GuideChatPanel({ open, turns, busy, closed = false, conversationId = null, suggestions, launcherRef, onClose, onSend, onFeedback, onSwitchToForm, onReset }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');

  const close = useCallback(() => {
    onClose();
    launcherRef.current?.focus();
  }, [onClose, launcherRef]);

  useFocusTrap(panelRef, open, close);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, open, closed]);

  if (!open) return null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || busy || closed) return;
    setInput('');
    onSend(question);
  };

  const lastResponse = [...turns].reverse().find((t) => t.response)?.response;
  const nextQuestion = closed ? null : lastResponse?.nextQuestion?.trim() || null;
  // The follow-up question is the assistant asking: chips then hold the visitor's possible answers, not questions.
  const quickReplies = nextQuestion ? quickRepliesFor(lastResponse?.nextQuestionField) : [];
  const chips = closed ? [] : nextQuestion ? quickReplies : suggestions;
  const chipsLabel = nextQuestion ? CHAT_LABELS.quickRepliesLabel : CHAT_LABELS.suggestionsLabel;
  const placeholder = closed ? CHAT_LABELS.inputPlaceholderClosed : nextQuestion ? CHAT_LABELS.inputPlaceholderAnswer : CHAT_LABELS.inputPlaceholder;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="guide-chat-title"
      aria-describedby="guide-chat-subtitle"
      className="fixed inset-0 z-[70] flex flex-col bg-white shadow-2xl md:inset-auto md:bottom-6 md:right-6 md:h-[620px] md:max-h-[calc(100dvh-3rem)] md:w-[400px] md:rounded-2xl md:border md:border-rm-blue-light"
    >
      <header className="flex items-center justify-between gap-2 border-b border-rm-gray-rule px-4 py-3">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-full bg-rm-blue-deep text-white">
            <i className="fa-solid fa-comment-question text-base" />
          </span>
          <div>
            <h2 id="guide-chat-title" className="font-recoleta text-base font-semibold text-pale">{CHAT_LABELS.headerTitle}</h2>
            <p id="guide-chat-subtitle" className="text-xs text-rm-gray-dark">{CHAT_LABELS.headerSubtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {(closed || turns.some((t) => t.question)) && (
            <button type="button" onClick={onReset} className="rounded px-2 py-1 text-xs text-rm-gray-dark underline hover:text-pale focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue-deep">
              {CHAT_LABELS.newConversation}
            </button>
          )}
          <button
            type="button"
            onClick={close}
            aria-label={CHAT_LABELS.closePanel}
            title={CHAT_LABELS.closePanelTitle}
            className="flex h-9 w-9 items-center justify-center rounded-full text-rm-gray-dark hover:bg-rm-gray-rule hover:text-pale focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue-deep"
          >
            <span aria-hidden="true" className="text-2xl leading-none">×</span>
          </button>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3" aria-live="polite" aria-relevant="additions text">
        {turns.length === 0 && (
          <p className="py-8 text-center text-sm text-rm-gray-dark">{CHAT_LABELS.emptyState}</p>
        )}
        <ol className="space-y-3">
          {turns.map((turn) => (
            <li key={turn.id} className="space-y-1.5">
              {turn.notice && <p className="text-center text-xs italic text-rm-gray-dark">{turn.notice}</p>}
              {turn.question && (
                <div className="flex justify-end">
                  <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-rm-blue-deep px-3 py-2 text-sm text-white">
                    <span className="sr-only">{CHAT_LABELS.visitorPrefix}</span>
                    {turn.question}
                  </p>
                </div>
              )}
              {turn.loading && (
                <p className="w-fit rounded-2xl rounded-bl-sm bg-rm-blue-pale px-3 py-2 text-sm text-rm-gray-dark" aria-label={CHAT_LABELS.loadingAria}>
                  <span className="animate-pulse">{CHAT_LABELS.loading}</span>
                </p>
              )}
              {turn.unavailable && <UnavailableNotice info={turn.unavailable} conversationId={conversationId ?? undefined} question={turn.question ?? undefined} />}
              {turn.error && !turn.unavailable && (
                <p className="max-w-[90%] rounded-2xl rounded-bl-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {CHAT_LABELS.error}
                </p>
              )}
              {turn.response && (
                <AssistantTurn turn={turn} response={turn.response} onFeedback={onFeedback} onSwitchToForm={onSwitchToForm} />
              )}
            </li>
          ))}
        </ol>
        {closed && (
          <div role="status" data-conversation-closed className="mt-3 rounded-xl border border-rm-gray-light bg-rm-gray-rule/40 px-3 py-2 text-center text-xs text-rm-gray-dark">
            <p>{CHAT_LABELS.conversationClosed}</p>
            <button type="button" onClick={onReset} className={`mt-2 ${secondaryButton}`}>
              {CHAT_LABELS.newConversation}
            </button>
          </div>
        )}
      </div>

      <div className="border-t border-rm-gray-rule px-4 pb-3 pt-2">
        {chips.length > 0 && !busy && (
          <ul className="mb-2 flex flex-wrap gap-1.5" aria-label={chipsLabel}>
            {chips.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onClick={() => onSend(s)}
                  className="rounded-full border border-rm-blue-deep bg-white px-3 py-1 text-left text-xs font-medium text-rm-blue-deep hover:bg-rm-blue-pale focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue-deep"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={submit} className="flex items-end gap-2">
          <label htmlFor="guide-chat-input" className="sr-only">{CHAT_LABELS.inputLabel}</label>
          <textarea
            id="guide-chat-input"
            data-autofocus
            value={input}
            disabled={closed}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(e);
              }
            }}
            rows={2}
            placeholder={placeholder}
            className="flex-1 resize-none rounded-lg border border-rm-gray-light px-3 py-2 text-sm text-pale placeholder:text-rm-gray-dark focus:border-rm-blue-deep focus:outline-none focus:ring-2 focus:ring-rm-blue-deep disabled:cursor-not-allowed disabled:bg-rm-gray-rule/40"
          />
          <button
            type="submit"
            disabled={busy || closed || !input.trim()}
            className="h-[42px] rounded-lg bg-rm-blue-deep px-4 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-rm-blue-deep"
          >
            {CHAT_LABELS.send}
          </button>
        </form>
        <p className="mt-2 text-[11px] leading-snug text-rm-gray-dark">{CHAT_LABELS.disclaimer}</p>
      </div>
    </div>
  );
}

function AssistantTurn({
  turn,
  response,
  onFeedback,
  onSwitchToForm,
}: {
  turn: ChatTurn;
  response: AskResponse;
  onFeedback: Props['onFeedback'];
  onSwitchToForm: Props['onSwitchToForm'];
}) {
  const citations = Array.isArray(response.citations) ? response.citations.filter((c) => typeof c.url === 'string') : [];
  const notice = noticeOf(response.subjectNotice);
  const subjectLink = subjectLinkOf(response, notice);
  const cta = quoteCtaOf(response.quoteCta);
  const card = isAppointmentCard(response.appointmentCard) ? response.appointmentCard : null;
  const phoneCta = phoneCtaOf(response.phoneCta);
  const prefillUrl = typeof response.prefillUrl === 'string' && response.prefillUrl ? response.prefillUrl : null;
  const prefillOffer = response.prefillOffer === true && !prefillUrl;

  return (
    <div className="space-y-1.5">
      <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-rm-blue-pale px-3 py-2">
        <span className="sr-only">{CHAT_LABELS.assistantPrefix}</span>
        <MarkdownLite text={withoutSourcesLine(response.answer ?? '')} />
        {!response.conversationClosed && typeof response.nextQuestion === 'string' && response.nextQuestion.trim() && (
          <p className="mt-1.5 text-sm font-medium text-pale">{response.nextQuestion.trim()}</p>
        )}
        {citations.length > 0 && (
          <details className="mt-1.5 text-xs text-rm-gray-dark">
            <summary className="cursor-pointer select-none underline hover:text-pale">{CHAT_LABELS.sources} ({citations.length})</summary>
            <ol className="mt-1 list-decimal space-y-0.5 pl-4">
              {citations.map((c, i) => (
                <li key={`${citationHref(c)}-${i}`}>
                  <a href={citationHref(c)} target="_blank" rel="noopener noreferrer" className="text-rm-blue-deep underline hover:text-pale">
                    {citationLabel(c)}
                  </a>
                </li>
              ))}
            </ol>
          </details>
        )}
      </div>
      {notice && (
        <div className="max-w-[92%] rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {notice.message}{' '}
          {subjectLink && (
            <a href={subjectLink} className="font-semibold underline">{CHAT_LABELS.subjectRedirectLink}</a>
          )}
        </div>
      )}
      {!notice && subjectLink && (
        <a href={subjectLink} className="inline-block text-xs font-semibold text-rm-blue-deep underline hover:text-pale">
          {CHAT_LABELS.subjectRedirectLink} <span aria-hidden="true">→</span>
        </a>
      )}
      {response.escalation?.transitionMessage && !card && (
        <p className="max-w-[92%] rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">{response.escalation.transitionMessage}</p>
      )}
      {phoneCta && !card?.phone && <PhoneCtaLink cta={phoneCta} />}
      {card && <AppointmentCard card={card} compact />}
      {cta && (
        <div className="max-w-[92%] rounded-xl border border-rm-blue-light bg-white px-3 py-2">
          {cta.title && <p className="text-sm font-semibold text-pale">{cta.title}</p>}
          {cta.message && <p className="mt-0.5 text-xs text-rm-gray-dark">{cta.message}</p>}
          <a href={cta.url} className="mt-2 inline-block rounded-lg bg-rm-orange px-3 py-1.5 text-sm font-bold text-white hover:opacity-90">
            {cta.label ?? CHAT_LABELS.quoteCtaDefaultLabel}
          </a>
        </div>
      )}
      {prefillUrl && (
        <button type="button" onClick={() => onSwitchToForm(prefillUrl)} className={secondaryButton}>
          {CHAT_LABELS.prefillButton} <span aria-hidden="true">→</span>
          <span className="block text-[11px] font-normal text-rm-gray-dark">{CHAT_LABELS.prefillHint}</span>
        </button>
      )}
      {prefillOffer && (
        <button type="button" data-prefill-offer onClick={() => onSwitchToForm(COMPARATOR_URL)} className={secondaryButton}>
          {CHAT_LABELS.prefillOfferButton} <span aria-hidden="true">→</span>
          <span className="block text-[11px] font-normal text-rm-gray-dark">{CHAT_LABELS.prefillOfferHint}</span>
        </button>
      )}
      {turn.question && (
      <div className="flex items-center gap-1 text-xs text-rm-gray-dark" role="group" aria-label={CHAT_LABELS.feedbackGroup}>
        <span>{CHAT_LABELS.feedbackQuestion}</span>
        {(['up', 'down'] as const).map((rating) => {
          const active = turn.feedback === rating;
          return (
            <button
              key={rating}
              type="button"
              aria-pressed={active}
              onClick={() => onFeedback(turn.id, rating)}
              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue-deep ${
                active ? 'border-rm-blue-deep bg-rm-blue-deep text-white' : 'border-rm-gray-light text-rm-gray-dark hover:border-rm-blue-deep hover:text-pale'
              }`}
            >
              <i aria-hidden="true" className={`fa-regular ${rating === 'up' ? 'fa-thumbs-up' : 'fa-thumbs-down'}`} />
              {rating === 'up' ? CHAT_LABELS.feedbackYes : CHAT_LABELS.feedbackNo}
            </button>
          );
        })}
        {turn.feedback && <span className="ml-1 italic">{CHAT_LABELS.feedbackThanks}</span>}
      </div>
      )}
    </div>
  );
}
